import { Component, Input, OnInit } from '@angular/core';
import { ModalController } from '@ionic/angular';
import { appLanguageOptions, HazardMeasureConstants, MachineAttributesManagementConstants } from 'src/app/utilities/constant';
import { getMeasureTypeTranslationKey } from 'src/app/utilities/utils';


@Component({
  selector: 'app-edit-add-ons',
  templateUrl: './edit-add-ons.component.html',
  styleUrls: ['./edit-add-ons.component.scss'],
})
export class EditAddOnsComponent implements OnInit {
  public HazardMeasureConstants = HazardMeasureConstants;
  public readonly getMeasureTypeTranslationKey = getMeasureTypeTranslationKey;
  @Input() action: any;
  @Input() title: any;
  @Input() data: any;
  /** Optional override for the modal header so callers can use singular labels (e.g. "Initial Hazard" / "Measure"). */
  @Input() headerTitle: string;
  /** Measure-type options used when the modal is opened in Add mode for Measures. */
  @Input() measureTypes: any[] = [];
  @Input() requireLanguageForSave = false;
  currentData: any[];
  isDataChange: boolean = false;

  /** Dropdown items rendered as 'English (en)', 'German (de)', etc. The bound value is the app-internal code. */
  public readonly languageOptions = appLanguageOptions.map(lang => ({
    code: lang.code,
    display: `${lang.label} (${lang.code})`
  }));

  private static readonly ATTRIBUTE_MAX_LENGTHS: { [key: string]: number } = {
    [MachineAttributesManagementConstants.SPECIFIC_FEATURES]: 60,
    [MachineAttributesManagementConstants.CONTROL_SYSTEM]: 30,
    [MachineAttributesManagementConstants.APPLICATION]: 500,
    [MachineAttributesManagementConstants.MACHINE_TYPE]: 60
  };

  constructor(private modalController: ModalController) { }

  ngOnInit() {
    this.currentData = JSON.parse(JSON.stringify(this.data));
    this.onDataChange();
  }

  /**
   * Returns the maximum allowed length for the current attribute title.
   * Returns null when no limit should be enforced.
   */
  public get maxLength(): number | null {
    return EditAddOnsComponent.ATTRIBUTE_MAX_LENGTHS[this.title] ?? null;
  }

  /**
   * Enforces the character limit by truncating the value if needed.
   * Returns the current length for the inline counter.
   */
  public getCurrentLength(item: any): number {
    return (item?.machineTitle || '').length;
  }

  public onTitleChange(item: any) {
    const limit = this.maxLength;
    if (limit !== null && item?.machineTitle && item.machineTitle.length > limit) {
      item.machineTitle = item.machineTitle.substring(0, limit);
    }
    this.onDataChange();
  }

  /**
   * To enable save button on data change
   */
  public onDataChange() {
    if (this.isAddMode) {
      this.isDataChange = this.currentData.some((item) => {
        const title = (item.machineTitle || '').trim();
        const languageCode = (item.languageCode || '').toString().trim();
        if (!title) {
          return false;
        }
        if (this.requireLanguageForSave && !languageCode) {
          return false;
        }
        if (this.title === HazardMeasureConstants.MEASURES) {
          return !!(item.measureType || '').toString().trim();
        }
        return true;
      });
      return;
    }
    this.isDataChange = this.currentData.some((item, index) => {
      const currentTitle = item.machineTitle?.trim() || '';
      const originalTitle = this.data[index]?.machineTitle?.trim() || '';
      const currentLang = (item.languageCode || '').trim();
      const originalLang = (this.data[index]?.languageCode || '').trim();
      const titleChanged = currentTitle !== originalTitle && currentTitle !== '';
      const languageChanged = currentLang !== originalLang;
      return titleChanged || languageChanged;
    });
  }

  /** True only for the hazard/measure edit flows where the language dropdown is shown. */
  public get showLanguageDropdown(): boolean {
    return this.title === HazardMeasureConstants.INITIAL_HAZARDS
      || this.title === HazardMeasureConstants.MEASURES;
  }

  /** True when creating a new entry — the modal was opened in Add mode. */
  public get isAddMode(): boolean {
    return this.action === true || this.action === 'true';
  }

  /** Show measure-type dropdown only when adding a new Measure. Editing keeps the original read-only display. */
  public get showMeasureTypeDropdown(): boolean {
    return this.isAddMode && this.title === HazardMeasureConstants.MEASURES;
  }

  /** Label shown in the modal header — singular for add flows, otherwise falls back to the tab title. */
  public get displayHeaderTitle(): string {
    return this.headerTitle || this.title;
  }

  /** Sync both name and id onto the row when a measure type is picked from the dropdown. */
  public onMeasureTypeChange(item: any, selected: any) {
    item.measureType = selected?.measure_type_name ?? null;
    item.measure_type_id = selected?.measure_type_id ?? '';
    this.onDataChange();
  }

  /** Resolves the currently-selected measure-type object so the ng-select reflects the row value. */
  public getSelectedMeasureType(item: any) {
    if (!item || !this.measureTypes?.length) {
      return null;
    }
    if (item.measure_type_id) {
      return this.measureTypes.find(t => t.measure_type_id === item.measure_type_id) ?? null;
    }
    if (item.measureType) {
      return this.measureTypes.find(t => t.measure_type_name === item.measureType) ?? null;
    }
    return null;
  }

  /**
   * Closes modal on clicking close icon
   */
  public closeModal() {
    this.modalController.dismiss({
      isActionConfirmed: false
    });
  }

  /**
   * Confirms confirmation modal component
   */
  public save() {
    this.modalController.dismiss({
      isActionConfirmed: true,
      dismissed: true,
      updatedData: this.currentData
    });
  }

}
